<template>
  <div class="card">
    <Panel header="Choose the environments:">
      <div class="flex gap-2 items-center py-2">
        <label for="switch1">From Heroku Connect configuration</label>
        <ToggleSwitch
          v-model="fromHerokuConnectCfg"
          input-id="switch1"
        />
      </div>
      <div class="flex flex-row gap-4 items-center">
        <FloatLabel
          v-if="!fromHerokuConnectCfg"
          class="w-1/2"
          variant="on"
        >
          <Select
            v-model="envSource"
            input-id="envSource"
            :options="envSourceOpts"
            option-label="label"
            option-value="value"
            class="w-full"
            show-clear
          />
          <label for="envSource">Source</label>
        </FloatLabel>
        <div
          v-else
          class="text-center flex items-center gap-2"
        >
          <FileUpload
            mode="basic"
            custom-upload
            auto
            severity="secondary"
            class="p-button-outlined"
            @select="onFileSelect"
          />
          <span>{{ fromHerokuConnectFileName }}</span>
        </div>
        <span>⮕</span>
        <FloatLabel
          class="w-1/2"
          variant="on"
        >
          <Select
            v-model="envTarget"
            input-id="envTarget"
            :options="envTargetOpts"
            option-label="label"
            option-value="value"
            class="w-full"
            show-clear
          />
          <label for="envTarget">Target</label>
        </FloatLabel>
      </div>
    </Panel>
    <div class="flex-grow flex min-h-0 border dark:border-[--p-panel-border-color] rounded-md p-2">
      <div
        v-if="!disableSave"
        class="absolute bottom-6 right-6 z-10"
      >
        <Button
          type="button"
          :rounded="true"
          size="large"
          class="scale-150"
          icon="pi pi-cloud-upload"
          @click="deployData()"
        />
      </div>
      <div
        v-if="loading"
        class="flex flex-1 items-center justify-center"
      >
        <ProgressSpinner />
      </div>
      <template v-else>
        <div
          class="h-full dark:border-r-[--p-datatable-body-cell-border-color]"
          :class="{'border-r': numColumns > 1}"
          :style="{ width: `calc(${100 / numColumns}% - 1px)` }"
        >
          <DataTable
            v-model:selection="selectedObject"
            scrollable
            scroll-height="flex"
            removable-sort
            selection-mode="single"
            :meta-key-selection="false"
            :value="Object.keys(deploymentData).sort().map(key => ({ key, value: deploymentData[key] }))"
          >
            <template #empty>
              Nothing to deploy
            </template>
            <Column
              :sortable="true"
              header="Objects"
              field="key"
              header-style="width: 100%"
            >
              <template #body="{ data }">
                <div class="flex flex-row justify-between">
                  <span class="text-indigo-600 dark:text-[#569cd6]">{{ data.key }}</span>
                  <Button
                    v-if="!data.value.inTargetEnv"
                    :label="selectedStuff[data.key] ? 'Added' : 'Add'"
                    :icon="selectedStuff[data.key] ? 'pi pi-check' : 'pi pi-plus'"
                    severity="success"
                    :variant="selectedStuff[data.key] ? 'text' : undefined"
                    size="small"
                    class="w-24 h-5"
                    @click="addObject(data.key, $event)"
                  />
                  <Button
                    v-if="!data.value.inSourceEnv"
                    :label="removedObjects.has(data.key) ? 'Removed' : 'Remove'"
                    :icon="removedObjects.has(data.key) ? 'pi pi-check' : 'pi pi-times'"
                    severity="danger"
                    :variant="removedObjects.has(data.key) ? 'text' : undefined"
                    size="small"
                    class="w-24 h-5"
                    @click="removeObject(data.key, $event)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
        <template v-for="(column, idx) of objectCols">
          <div
            v-if="selectedObject?.value?.[column.col].length"
            :key="column.col"
            class="h-full dark:border-r-[--p-datatable-body-cell-border-color]"
            :class="{'border-r': idx < objectCols.length - 1}"
            :style="{ width: `calc(${100 / numColumns}% - 1px)` }"
          >
            <DataTable
              v-model:selection="selectedObject"
              scrollable
              removable-sort
              scroll-height="flex"
              :meta-key-selection="false"
              :row-style="(data) => rowStyle(data, column.col)"
              :value="selectedObject.value[column.col].sort().map(x => ({apiName: x, obj: selectedObject.key}))"
              :select-all="allSelected[column.col]"
              @select-all-change="selectAll(column.col)"
            >
              <Column
                :sortable="true"
                :header="column.label"
                field="apiName"
                :selection-mode="selectedStuff[selectedObject?.key] && !removedObjects.has(selectedObject?.key)? 'multiple' : null"
                class="overflow-x-hidden"
              >
                <template #body="{ data }">
                  <div
                    class="flex w-full gap-4 items-center"
                  >
                    <Checkbox
                      v-if="selectedStuff[selectedObject?.key] && !removedObjects.has(selectedObject?.key)"
                      v-model="selectedStuff[selectedObject?.key][column.col]"
                      name="selected"
                      :value="data.apiName"
                      :disabled="!selectedStuff[selectedObject?.key] || ['SystemModstamp', 'Id', 'IsDeleted'].includes(data.apiName)"
                      @change="changeCheckbox(selectedObject?.key, column.col, data.apiName, $event.target.checked)"
                    />
                    <Checkbox
                      v-else
                      name="selected"
                      binary
                      :default-value="removedObjects.has(selectedObject?.key)|| ['SystemModstamp', 'Id', 'IsDeleted'].includes(data.apiName)"
                      :disabled="true"
                    />
                    <span>{{ data.apiName }}</span>
                  </div>
                </template>
              </Column>
            </DataTable>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
<script setup>
import api from '@/lib/api'
import { useDark } from '@vueuse/core'
import { useToast } from 'primevue/usetoast'
import { computed, ref, watchEffect } from 'vue'

const envSource = ref(undefined)
const envTarget = ref(undefined)
const fromHerokuConnectCfg = ref(false)
const fromHerokuConnectPayload = ref()
const fromHerokuConnectFileName = ref()
watchEffect(() => {
  if (!fromHerokuConnectCfg.value) {
    fromHerokuConnectPayload.value = undefined
    fromHerokuConnectFileName.value = undefined
  } else {
    envSource.value = undefined
  }
})

const envOpts = ref([])
const selectedObject = ref()
const deploymentData = ref({})
const selectedStuff = ref({})
const removedObjects = ref(new Set())
const isDark = useDark()
const toast = useToast()
const loading = ref(false)
const numColumns = computed(() => {
  return 1 +
    (selectedObject?.value?.value?.addColumns.length ? 1 : 0) +
    (selectedObject?.value?.value?.dropColumns.length ? 1 : 0) +
    (selectedObject?.value?.value?.addIndexes.length ? 1 : 0) +
    (selectedObject?.value?.value?.dropIndexes.length ? 1 : 0)
})

const onFileSelect = async (event) => {
  const file = event.files[0]
  fromHerokuConnectFileName.value = file.name
  const reader = new FileReader()
  reader.onload = async (e) => {
    const text = e.target.result
    fromHerokuConnectPayload.value = JSON.parse(text.toString())
  }
  reader.readAsText(file)
}

const deployPayload = computed(() => {
  return Object.fromEntries(Object.entries(selectedStuff.value).filter(([key, value]) => value.addColumns.length || value.dropColumns.length || value.addIndexes.length || value.dropIndexes.length))
})

const disableSave = computed(() => {
  return loading.value || (!envSource.value && (!fromHerokuConnectCfg.value || !fromHerokuConnectPayload.value)) || !envTarget.value || (!Object.keys(deployPayload.value).length && !removedObjects.value.size)
})

const deployData = async () => {
  if (disableSave.value) return
  loading.value = true
  try {
    deployPayload.value.objectsToRemove = Array.from(removedObjects.value)
    await api.deploy.deploy(envSource.value, envTarget.value, deployPayload.value, toast)
    await fetchData()
  } finally {
    loading.value = false
  }
}

const objectCols = computed(() => [
  { col: 'addColumns', label: 'Add Columns' },
  { col: 'dropColumns', label: 'Drop Columns' },
  { col: 'addIndexes', label: 'Add Indexes' },
  { col: 'dropIndexes', label: 'Drop Indexes' }
].filter(x => selectedObject?.value?.value?.[x.col].length))

const fetchData = async () => {
  if ((envSource.value || (fromHerokuConnectCfg.value && fromHerokuConnectPayload.value)) && envTarget.value) {
    loading.value = true
    if (fromHerokuConnectCfg.value) {
      deploymentData.value = await api.deploy.getDeployInfoFromHerokuConnect(fromHerokuConnectPayload.value, envTarget.value)
    } else {
      deploymentData.value = await api.deploy.getDeployInfo(envSource.value, envTarget.value)
    }
    selectedStuff.value = {}
    selectedObject.value = null
    for (const key in deploymentData.value) {
      if (deploymentData.value[key].inTargetEnv) {
        selectedStuff.value[key] = { addColumns: [], dropColumns: [], addIndexes: [], dropIndexes: [] }
      }
    }
    loading.value = false
  } else {
    selectedObject.value = null
    deploymentData.value = {}
  }
}

watchEffect(fetchData)

const rowStyle = (data, col) => {
  const hasBeenAdded = selectedStuff.value?.[selectedObject.value.key]?.[col].includes(data.apiName) || removedObjects.value.has(selectedObject.value.key)
  return {
    // '#86efac'
    'background-color': hasBeenAdded ? (isDark.value ? 'rgb(134, 239, 172, 0.8)' : '#f3fcf9') : undefined,
    color: isDark.value && (hasBeenAdded) ? '#000' : undefined
  }
}

const allSelected = computed(() => {
  const areAllSelected = (col) => (selectedObject.value?.value?.[col].length - (['dropColumns', 'dropIndexes'].includes(col) ? 3 : 0)) <= (selectedStuff?.value?.[selectedObject.value.key]?.[col].length)
  return {
    addColumns: areAllSelected('addColumns'),
    dropColumns: areAllSelected('dropColumns'),
    addIndexes: areAllSelected('addIndexes'),
    dropIndexes: areAllSelected('dropIndexes')
  }
})

const selectAll = (col, overrideObj) => {
  const shouldDeselectAll = !overrideObj && allSelected.value[col]
  const obj = overrideObj || selectedObject.value.key
  selectedStuff.value[obj][col] = shouldDeselectAll ? deploymentData.value[obj][col].filter(el => ['addColumns', 'addIndexes'].includes(col) && ['SystemModstamp', 'Id', 'IsDeleted'].includes(el)) : deploymentData.value[obj][col].filter(el => ['addColumns', 'addIndexes'].includes(col) || !['SystemModstamp', 'Id', 'IsDeleted'].includes(el))
  switch (col) {
    case 'addColumns':
      if (shouldDeselectAll) {
        selectedStuff.value[obj].addIndexes = deploymentData.value[obj].addIndexes.filter(el => ['SystemModstamp', 'Id', 'IsDeleted'].includes(el))
      }
      break
    case 'dropColumns':
      selectedStuff.value[obj].dropIndexes = shouldDeselectAll ? [] : deploymentData.value[obj].dropIndexes.filter(el => !['SystemModstamp', 'Id', 'IsDeleted'].includes(el))
      break
    case 'addIndexes':
      if (!shouldDeselectAll) {
        selectedStuff.value[obj].addColumns = [...new Set([...deploymentData.value[obj][col], ...selectedStuff.value[obj].addColumns])]
      }
      break
    default:
      break
  }
}

const envSourceOpts = computed(() => {
  return envOpts.value.filter((env) => env.value !== envTarget.value)
})

const envTargetOpts = computed(() => {
  return envOpts.value.filter((env) => env.value !== envSource.value)
})

const addObject = (key, event) => {
  if (!selectedStuff.value[key]) {
    selectedStuff.value[key] = { addColumns: [], dropColumns: [], addIndexes: [], dropIndexes: [], sourceSyncMinutes: deploymentData.value[key].sourceSyncMinutes }
    selectAll('addColumns', key)
    selectAll('addIndexes', key)
  } else {
    delete selectedStuff.value[key]
  }
  event.stopPropagation()
}

const removeObject = (key, event) => {
  if (!removedObjects.value.has(key)) {
    removedObjects.value.add(key)
  } else {
    removedObjects.value.delete(key)
  }
  event.stopPropagation()
}

const changeCheckbox = (object, type, field, checked) => {
  switch (type) {
    case 'addColumns':
      if (!checked) {
        selectedStuff.value[object].addIndexes = selectedStuff.value[object].addIndexes.filter(f => f !== field)
      }
      break
    case 'dropColumns':
      if (checked && deploymentData.value[object].dropIndexes.includes(field)) {
        selectedStuff.value[object].dropIndexes.push(field)
      }
      break
    case 'addIndexes':
      if (checked && deploymentData.value[object].addColumns.includes(field)) {
        selectedStuff.value[object].addColumns.push(field)
      }
      break
    default:
      break
  }
}

api.settings.getEnvironments().then(res => {
  envOpts.value = res
})
</script>
<style scoped>
* {
  --p-checkbox-transition-duration: 0s;
  --p-datatable-transition-duration: 0s;
}
.p-datatable-scrollable-table>.p-datatable-tfoot {
  bottom: -1px;
}
</style>
